import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { ListSkeleton } from "@/components/Skeletons";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, Trash2, Search, Languages, Loader2, FileText } from "lucide-react";
import { cleanNarrative } from "./wevaUtils";

const TIMEFRAMES = [{ v: 7, l: "7d" }, { v: 30, l: "30d" }, { v: 90, l: "90d" }, { v: 365, l: "1y" }];
const RANGES = ["all", "30d", "90d", "1y"];
const REPORT_LANGS = ["de", "en", "es", "fr"];

function TimeframePicker({ value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {TIMEFRAMES.map((o) => (
        <button key={o.v} type="button" onClick={() => !disabled && onChange(o.v)} disabled={disabled}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${value === o.v && !disabled ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"} ${disabled ? "opacity-40" : ""}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function SourceRow({ label, enabled, onToggle, days, onDays, allMode, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-foreground">{label}</span>
        <Switch checked={enabled} onCheckedChange={onToggle} disabled={allMode} />
      </div>
      {enabled && !allMode ? <div className="mt-3"><TimeframePicker value={days} onChange={onDays} /></div> : null}
    </div>
  );
}

export default function WevaReports() {
  const { t, lang } = useI18n();
  usePageMeta(t("weva_reports_title"));
  const navigate = useNavigate();

  const [reports, setReports] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [scopeAll, setScopeAll] = useState(true);
  const [allDays, setAllDays] = useState(30);
  const [reportLang, setReportLang] = useState(REPORT_LANGS.includes(lang) ? lang : "en");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all");

  const [incR, setIncR] = useState(true); const [dR, setDR] = useState(30);
  const [incD, setIncD] = useState(true); const [dD, setDD] = useState(30);
  const [incL, setIncL] = useState(true); const [dL, setDL] = useState(30);
  const [incB, setIncB] = useState(true); const [dB, setDB] = useState(30);
  const [incG, setIncG] = useState(true); const [dG, setDG] = useState(30);
  const [incCZ, setIncCZ] = useState(true); const [dCZ, setDCZ] = useState(30);
  const [incCP, setIncCP] = useState(true); const [dCP, setDCP] = useState(30);
  const [incJ, setIncJ] = useState(true); const [dJ, setDJ] = useState(30);
  const [incBel, setIncBel] = useState(true); const [dBel, setDBel] = useState(30);
  const [incUL, setIncUL] = useState(true); const [dUL, setDUL] = useState(30);
  const [incP, setIncP] = useState(false); const [dP, setDP] = useState(90);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/weva/reports"); setReports(Array.isArray(data) ? data : []); }
    catch { setReports([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = Date.now();
    const rangeMs = range === "30d" ? 30 * 864e5 : range === "90d" ? 90 * 864e5 : range === "1y" ? 365 * 864e5 : Infinity;
    return (reports || []).filter((r) => {
      if (q) {
        const hay = ((r.narrative || "") + " " + (r.sections || []).map((s) => (s.heading || "") + " " + (s.bullets || []).join(" ")).join(" ")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (rangeMs !== Infinity && r.created_at) { if (now - new Date(r.created_at).getTime() > rangeMs) return false; }
      return true;
    });
  }, [reports, query, range]);

  const generate = async () => {
    setGenerating(true);
    try {
      const body = {
        scope_all: scopeAll, all_days: allDays,
        include_realize: incR, realize_days: dR,
        include_designate: incD, designate_days: dD,
        include_release: incL, release_days: dL,
        include_breakouts: incB, breakouts_days: dB,
        include_goals: incG, goals_days: dG,
        include_comfortzone: incCZ, comfortzone_days: dCZ,
        include_coping: incCP, coping_days: dCP,
        include_journal: incJ, journal_days: dJ,
        include_beliefs: incBel, beliefs_days: dBel,
        include_upper_limits: incUL, upper_limits_days: dUL,
        include_prior: incP, prior_days: dP,
        language: reportLang,
      };
      const { data } = await api.post("/weva/reports/generate", body);
      if (data?.id) navigate(`/app/weva/report/${data.id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        if (window.confirm(t("weva_credits_needed"))) navigate("/app/credits");
      } else if (e?.response?.status === 400) {
        toast.error(t("weva_no_data"));
      } else { toast.error(apiError(e)); }
    } finally { setGenerating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete_weva_report_body"))) return;
    try { await api.delete(`/weva/reports/${id}`); setReports((r) => r.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e, t("popup_delete_failed"))); }
  };

  return (
    <div className="space-y-5" data-testid="weva-reports">
      <button onClick={() => navigate("/app/weva")} data-testid="weva-reports-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("weva")}
      </button>
      <div>
        <h1 className="font-display text-2xl text-foreground">{t("weva_reports_title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("weva_reports_sub")}</p>
      </div>

      <div className="lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
      <div className="space-y-5">
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-display text-sm text-foreground">{t("weva_reports_scope_all")}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t("one_timeframe_applied_to_all_sources")}</p>
          </div>
          <Switch checked={scopeAll} onCheckedChange={setScopeAll} data-testid="toggle-scope-all" />
        </div>
        {scopeAll ? <div className="mt-3"><TimeframePicker value={allDays} onChange={setAllDays} /></div> : null}
      </div>

      <div className="space-y-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("weva_scope_sources")}</p>
        <SourceRow label={t("weva_reports_include_realize")} enabled={incR} onToggle={setIncR} days={dR} onDays={setDR} allMode={scopeAll} testId="src-realize" />
        <SourceRow label={t("weva_reports_include_designate")} enabled={incD} onToggle={setIncD} days={dD} onDays={setDD} allMode={scopeAll} testId="src-designate" />
        <SourceRow label={t("weva_reports_include_release")} enabled={incL} onToggle={setIncL} days={dL} onDays={setDL} allMode={scopeAll} testId="src-release" />
        <SourceRow label={t("weva_reports_include_breakouts")} enabled={incB} onToggle={setIncB} days={dB} onDays={setDB} allMode={scopeAll} testId="src-breakouts" />
        <SourceRow label={t("weva_reports_include_goals")} enabled={incG} onToggle={setIncG} days={dG} onDays={setDG} allMode={scopeAll} testId="src-goals" />
        <SourceRow label={t("weva_reports_include_comfortzone")} enabled={incCZ} onToggle={setIncCZ} days={dCZ} onDays={setDCZ} allMode={scopeAll} testId="src-comfortzone" />
        <SourceRow label={t("weva_reports_include_coping")} enabled={incCP} onToggle={setIncCP} days={dCP} onDays={setDCP} allMode={scopeAll} testId="src-coping" />
        <SourceRow label={t("weva_reports_include_journal")} enabled={incJ} onToggle={setIncJ} days={dJ} onDays={setDJ} allMode={scopeAll} testId="src-journal" />
        <SourceRow label={t("my_beliefs_section_title")} enabled={incBel} onToggle={setIncBel} days={dBel} onDays={setDBel} allMode={scopeAll} testId="src-beliefs" />
        <SourceRow label={t("upper_limits_section_title")} enabled={incUL} onToggle={setIncUL} days={dUL} onDays={setDUL} allMode={scopeAll} testId="src-upper" />
        <SourceRow label={t("weva_reports_include_prior")} enabled={incP} onToggle={setIncP} days={dP} onDays={setDP} allMode={false} testId="src-prior" />
      </div>

      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4" data-testid="weva-lang-picker">
        <div className="mb-2 flex items-center gap-2">
          <Languages className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium text-foreground">{t("weva_report_language")}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {REPORT_LANGS.map((code) => (
            <button key={code} type="button" onClick={() => setReportLang(code)} data-testid={`weva-lang-${code}`}
              className={`min-w-[48px] rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${reportLang === code ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>
              {code.toUpperCase()}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">{t("weva_lang_picker_hint")}</p>
      </div>

      <Button onClick={generate} disabled={generating} data-testid="weva-generate" className="w-full gap-2 rounded-full">
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {generating ? t("weva_generating") : t("weva_reports_generate")}
      </Button>
      </div>

      <div className="mt-6 space-y-3 lg:mt-0">
        <h2 className="font-display text-lg text-foreground">{t("weva_reports_history")}</h2>
        {reports && reports.length > 0 && (
          <div className="space-y-2.5 rounded-2xl border border-border bg-card p-3">
            <div className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search_reports")} data-testid="report-history-search" className="w-full bg-transparent text-sm outline-none" />
            </div>
            <div className="flex gap-1.5">
              {RANGES.map((k) => (
                <button key={k} type="button" onClick={() => setRange(k)} data-testid={`report-history-range-${k}`}
                  className={`flex-1 rounded-full border px-2 py-1 text-xs font-medium transition-colors ${range === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>
                  {k === "all" ? "All" : k}
                </button>
              ))}
            </div>
          </div>
        )}

        {reports === null ? (
          <ListSkeleton rows={3} />
        ) : reports.length === 0 ? (
          <EmptyState icon={FileText} title={t("weva_reports_history")} description={t("your_reports_will_live_here")} />
        ) : filtered.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">{t("no_reports_match_your_filters")}</p>
        ) : filtered.map((r) => (
          <div key={r.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`report-${r.id}`}>
            <button onClick={() => navigate(`/app/weva/report/${r.id}`)} className="min-w-0 flex-1 text-left">
              <p className="text-xs text-muted-foreground">{shortDate(r.created_at)}</p>
              <p className="mt-0.5 line-clamp-2 text-sm text-foreground">{cleanNarrative(r.narrative)}</p>
            </button>
            <button onClick={() => remove(r.id)} data-testid={`report-delete-${r.id}`} className="shrink-0 rounded-lg p-2 text-rose-600 transition-colors hover:bg-rose-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
